import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from '@/components/LocalizedLink';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import MagneticButton from '@/components/MagneticButton';
import { fetchPublishedBlogPosts } from '@/lib/contentService';
import { resolveLocalizedValue } from '@/lib/blogLocalization';
import { getBlogCoverImage } from '@/lib/blogCovers';
import { getBlogLogicalPath } from '@/lib/blogSlugs';
import BlogCardMedia from '@/components/blog/BlogCardMedia';

const BlogSection = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const { data } = useQuery({
    queryKey: ['blog', 'homepage'],
    queryFn: fetchPublishedBlogPosts,
  });

  const posts = (data ?? []).slice(0, 3);

  return (
    <section ref={ref} className="section-y relative" id="blog">
      <div className="container mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="section-head flex items-end justify-between"
        >
          <div>
            <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">{t('blog.tag')}</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground">{t('blog.title')}</h2>
          </div>
          <MagneticButton>
            <Link to="/blog" className="hidden md:inline-block font-body text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t('blog.all')} →
            </Link>
          </MagneticButton>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <Link key={post.id} to={getBlogLogicalPath(post, lang)}>
              {(() => {
                const categoryDisplay = resolveLocalizedValue(post.category_i18n, post.category, lang);
                const titleDisplay = resolveLocalizedValue(post.title_i18n, post.title, lang);
                const excerptDisplay = resolveLocalizedValue(post.excerpt_i18n, post.excerpt, lang);
                const readingTime = resolveLocalizedValue(post.reading_time_i18n, '', lang);

                return (
              <motion.article
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6, transition: { type: 'spring', stiffness: 300 } }}
                className="group cursor-pointer"
              >
                <BlogCardMedia image={getBlogCoverImage(post)} alt={titleDisplay} category={categoryDisplay} />
                <time className="font-body text-xs text-muted-foreground">
                  {new Date(post.published_at || post.created_at).toLocaleDateString(
                    lang === 'tr' ? 'tr-TR' : 'en-US',
                  )}
                </time>
                {readingTime ? (
                  <span className="font-body text-xs text-muted-foreground ml-2">• {readingTime}</span>
                ) : null}
                <h3 className="font-display text-lg font-semibold text-foreground mt-2 group-hover:text-foreground/70 transition-colors duration-300">
                  {titleDisplay}
                </h3>
                <p className="font-body text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-2">
                  {excerptDisplay}
                </p>
              </motion.article>
                );
              })()}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
